import { motion } from "framer-motion";
import { Check, CalendarCheck, Database, MessageSquare } from "lucide-react";
import { PrimaryCTA, GhostCTA, CheckItem, Chip, Phone, StatusBar, Avatar, Parallax } from "../components/bits";

const ease = [0.22, 1, 0.36, 1];

const MiniDatabase = () => (
    <Phone className="w-[150px] sm:w-[168px]">
        <StatusBar />
        <div className="px-3.5 pb-4 pt-1">
            <div className="flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-[0.18em] text-ink/50">
                <Database className="h-2.5 w-2.5" /> Client Database
            </div>
            {["Sarah M.", "Elena R.", "Priya K.", "Anna T.", "Julia W."].map((n, i) => (
                <div
                    key={n}
                    className={`mt-1.5 flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                        i === 0 ? "border-rose/60 bg-blush" : "border-linen bg-ivory"
                    }`}
                >
                    <Avatar initials={n[0]} tone={i === 0 ? "bg-rose/40 text-ink" : "bg-sand text-mute"} />
                    <div className="min-w-0">
                        <div className="truncate font-sans text-[9px] font-medium text-ink">{n}</div>
                        <div className="font-mono text-[7px] uppercase tracking-wider text-faint">
                            {i === 0 ? "Due — 12 mo" : `${8 + i * 3} mo ago`}
                        </div>
                    </div>
                    {i === 0 && <span className="ml-auto h-1.5 w-1.5 animate-pulse-soft rounded-full bg-rose" />}
                </div>
            ))}
        </div>
    </Phone>
);

const MiniSms = () => (
    <Phone className="w-[190px] sm:w-[230px]">
        <StatusBar />
        <div className="px-3.5 pb-5 pt-1">
            <div className="flex items-center gap-2 border-b border-linen pb-2">
                <Avatar initials="R" tone="bg-rose text-white ring-2 ring-rose/30" />
                <div>
                    <div className="font-sans text-[10px] font-medium text-ink">Rose Clinic</div>
                    <div className="font-mono text-[7px] uppercase tracking-widest text-rose">Follow-up sent ✓</div>
                </div>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6, ease }}
                className="mt-2.5 max-w-[85%] rounded-xl rounded-tl-sm bg-white p-2.5 shadow-sm"
            >
                <p className="font-sans text-[9px] leading-snug text-ink">
                    Hi Sarah — it’s been almost a year since your last lip filler. We’d love to see you again.
                </p>
                <div className="mt-1.5 rounded-md bg-blush px-2 py-1 text-center font-mono text-[7px] uppercase tracking-[0.16em] text-ink">
                    10% off your next visit
                </div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.9, duration: 0.6, ease }}
                className="ml-auto mt-2 max-w-[75%] rounded-xl rounded-tr-sm bg-ink p-2.5"
            >
                <p className="font-sans text-[9px] leading-snug text-cream">
                    Yes please 🤍 Do you have anything next week?
                </p>
            </motion.div>
        </div>
    </Phone>
);

const MiniCalendar = () => (
    <Phone className="w-[150px] sm:w-[168px]">
        <StatusBar />
        <div className="px-3.5 pb-4 pt-1">
            <div className="flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-[0.18em] text-ink/50">
                <CalendarCheck className="h-2.5 w-2.5" /> July 2026
            </div>
            <div className="mt-2 grid grid-cols-7 gap-1">
                {Array.from({ length: 28 }).map((_, i) => (
                    <span
                        key={i}
                        className={`flex aspect-square items-center justify-center rounded font-mono text-[7px] ${
                            i === 17
                                ? "animate-glow bg-ink font-semibold text-cream"
                                : "bg-ivory text-ink/40"
                        }`}
                    >
                        {i + 1}
                    </span>
                ))}
            </div>
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 2.6, duration: 0.5, ease }}
                className="mt-2.5 flex items-center justify-center gap-1.5 rounded-lg bg-ink py-2"
            >
                <Check className="h-3 w-3 text-gold" strokeWidth={2.5} />
                <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-cream">Booked · Thu 2:30</span>
            </motion.div>
        </div>
    </Phone>
);

const Connector = () => (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" fill="none" aria-hidden>
        <path
            id="hero-path"
            d="M 60 300 C 200 180, 320 420, 480 260 S 700 160, 780 300"
            stroke="#D8A7A1"
            strokeOpacity="0.45"
            strokeWidth="1.2"
            className="line-flow"
        />
        <circle r="3.5" fill="#CBA368">
            <animateMotion dur="5s" repeatCount="indefinite">
                <mpath href="#hero-path" />
            </animateMotion>
        </circle>
    </svg>
);

const HeroVisual = () => (
    <div className="relative mx-auto h-[480px] w-full max-w-[560px] sm:h-[540px]" data-testid="hero-visual">
        <Connector />
        <motion.div
            initial={{ opacity: 0, y: 40, rotate: -8 }}
            animate={{ opacity: 1, y: 0, rotate: -6 }}
            transition={{ duration: 1, delay: 0.2, ease }}
            className="absolute left-0 top-14 hidden sm:block"
        >
            <Parallax speed={38}>
            <div className="animate-float">
                <MiniDatabase />
            </div>
            </Parallax>
        </motion.div>
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45, ease }}
            className="absolute left-1/2 top-0 z-10 -translate-x-1/2 sm:left-auto sm:right-24 sm:translate-x-0"
        >
            <Parallax speed={16}>
            <div className="animate-float-soft" style={{ animationDelay: "0.8s" }}>
                <MiniSms />
            </div>
            </Parallax>
        </motion.div>
        <motion.div
            initial={{ opacity: 0, y: 40, rotate: 8 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ duration: 1, delay: 0.7, ease }}
            className="absolute bottom-4 right-0 hidden sm:block"
        >
            <Parallax speed={52}>
            <div className="animate-float" style={{ animationDelay: "1.6s" }}>
                <MiniCalendar />
            </div>
            </Parallax>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }} className="relative z-20">
            <Chip className="absolute left-2 top-2 animate-float-soft sm:left-6" >12 months</Chip>
            <Chip className="absolute -right-1 top-16 animate-float sm:right-0" >Return window</Chip>
            <Chip className="absolute bottom-40 left-4 hidden animate-float-soft md:inline-flex">Follow-up sent ✓</Chip>
            <Chip className="absolute bottom-24 right-6 hidden animate-float bg-blush sm:inline-flex">Reactivated</Chip>
            <Chip className="absolute bottom-6 left-1/3 hidden animate-float-soft sm:inline-flex">10% offer</Chip>
            <Chip className="absolute right-4 top-1/2 hidden animate-float lg:inline-flex">
                Booked <Check className="h-3 w-3 text-gold" strokeWidth={2.5} />
            </Chip>
        </motion.div>

        <div className="absolute -bottom-2 left-0 hidden items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-faint sm:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-rose" /> Client database
            <span className="text-gold">→</span> Follow-up
            <span className="text-gold">→</span> Reply
            <span className="text-gold">→</span> Booked
        </div>
    </div>
);

export const Hero = () => (
    <section data-testid="hero-section" className="relative overflow-hidden pt-28 lg:pt-32">
        <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-blush/50 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:pb-20">
            <div>
                <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
                    <div className="flex items-center gap-3">
                        <span className="h-px w-8 bg-gold" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold" data-testid="hero-eyebrow">
                            Client reactivation for cosmetic + aesthetic clinics
                        </span>
                    </div>
                </motion.div>
                <motion.h1
                    initial={{ opacity: 0, y: 26 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.85, delay: 0.1, ease }}
                    className="mt-5 font-serif text-4xl uppercase leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
                    data-testid="hero-headline"
                >
                    Fill your <span className="italic text-rose">calendar</span> without
                    chasing new clients.
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.22, ease }}
                    className="mt-5 max-w-xl text-sm leading-relaxed text-mute sm:text-base"
                    data-testid="hero-subheadline"
                >
                    Summercrest helps cosmetic + aesthetic clinics automatically bring past clients back onto
                    the calendar — without spending more on ads to find new ones.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.34, ease }}
                    className="mt-8 flex flex-wrap items-center gap-4"
                >
                    <PrimaryCTA testId="hero-book-call-btn">Book a Reactivation Call</PrimaryCTA>
                    <GhostCTA href="#how-it-works" testId="hero-see-how-btn">
                        See How It Works
                    </GhostCTA>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="mt-8 flex flex-col gap-3 border-t border-linen pt-6 sm:flex-row sm:flex-wrap sm:gap-x-8"
                    data-testid="hero-proof-points"
                >
                    <CheckItem>No extra ad spend</CheckItem>
                    <CheckItem>Built around your existing client database</CheckItem>
                    <CheckItem>Every rebooking can be tracked</CheckItem>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.65 }}
                    className="mt-8 hidden items-center gap-6 lg:flex"
                >
                    {[
                        { icon: Database, label: "Database" },
                        { icon: MessageSquare, label: "Follow-up" },
                        { icon: CalendarCheck, label: "Rebooked" },
                    ].map((s, i) => (
                        <div key={s.label} className="flex items-center gap-6">
                            {i > 0 && <span className="h-px w-8 bg-rose/50" />}
                            <div className="flex items-center gap-2">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-linen bg-ivory">
                                    <s.icon className="h-3.5 w-3.5 text-ink/70" strokeWidth={1.5} />
                                </span>
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint">{s.label}</span>
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
            <HeroVisual />
        </div>
    </section>
);
